"use client";

import { useCallback, useState } from "react";
import type { PosterFetchScope, TmdbPosterPreview } from "../types";

export interface UseTmdbPosterPreviewResult {
  previewData: TmdbPosterPreview[] | null;
  loading: boolean;
  error: string | null;
  fetchPreview: (scope?: PosterFetchScope) => Promise<void>;
  reset: () => void;
}

/**
 * Fetches TMDB poster preview for an award show's nominees.
 * Accepts a fetcher so the hook is framework-agnostic (Next.js server action or API call).
 * @param awardShowId - Award show ID
 * @param fetchPreviewFn - Function that returns { nominees: TmdbPosterPreview[] }
 */
export function useTmdbPosterPreview(
  awardShowId: string,
  fetchPreviewFn: (
    awardShowId: string,
    scope: PosterFetchScope
  ) => Promise<{ nominees: TmdbPosterPreview[] }>
): UseTmdbPosterPreviewResult {
  const [previewData, setPreviewData] = useState<TmdbPosterPreview[] | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setPreviewData(null);
    setError(null);
  }, []);

  const fetchPreview = useCallback(
    async (scope: PosterFetchScope = "missing") => {
      setLoading(true);
      setError(null);
      setPreviewData(null);
      try {
        const result = await fetchPreviewFn(awardShowId, scope);
        setPreviewData(result.nominees);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch posters"
        );
      } finally {
        setLoading(false);
      }
    },
    [awardShowId, fetchPreviewFn]
  );

  return { previewData, loading, error, fetchPreview, reset };
}
